import type {
  ClaimReadinessTaskControlState,
  WorkflowClaimReadinessResponse,
  WorkflowImpactSummary,
} from '../../services/reportsApi';
import { reportsApi } from '../../services/reportsApi';

export type GroupedBlockerSyncType =
  | 'verification_method'
  | 'observation_coverage_reliability'
  | 'field_confirmation_of_zone_controls';

const VERIFICATION_METHOD_LABELS: Record<string, string> = {
  matched_window_measurement: 'Matched-window follow-up measurement',
  representative_evidence_review: 'Representative evidence review',
  field_walkdown: 'Field walkdown / control verification',
  combined: 'Combined measurement + evidence review',
};

export function formatVerificationMethodLabel(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return VERIFICATION_METHOD_LABELS[raw.toLowerCase()] || raw.replace(/_/g, ' ');
}

export function mergedValidationControlPayload(
  controlState?: ClaimReadinessTaskControlState | null,
): Record<string, any> {
  return {
    ...(controlState?.control_payload || {}),
    ...(controlState?.result_payload || {}),
  };
}

export function readLiveCoverageFields(
  workflowImpactSummary?: WorkflowImpactSummary | null,
): {
  denominator_trust: string | null;
  baseline_observation_coverage_percent: number | null;
  followup_observation_coverage_percent: number | null;
} {
  const impactMeasurement = workflowImpactSummary?.impact_measurement;
  return {
    denominator_trust: impactMeasurement?.denominator_trust != null
      ? String(impactMeasurement.denominator_trust)
      : null,
    baseline_observation_coverage_percent: impactMeasurement?.baseline_observation_coverage_percent ?? null,
    followup_observation_coverage_percent: impactMeasurement?.followup_observation_coverage_percent ?? null,
  };
}

export function buildBlockerSyncResultPayload(
  blockerType: GroupedBlockerSyncType,
  controlState: ClaimReadinessTaskControlState | null | undefined,
  attestationPatch: Record<string, any>,
  workflowImpactSummary?: WorkflowImpactSummary | null,
): Record<string, any> {
  const payload = mergedValidationControlPayload(controlState);
  if (blockerType === 'verification_method') {
    const verificationMethod = String(
      attestationPatch.verification_method
      || payload.verification_method
      || '',
    ).trim();
    return {
      verification_method: verificationMethod,
      derived_from_live_workflow: attestationPatch.derived_from_live_workflow === true,
      ...attestationPatch,
    };
  }
  if (blockerType === 'observation_coverage_reliability') {
    const liveCoverage = readLiveCoverageFields(workflowImpactSummary);
    return {
      review_status: attestationPatch.review_status ?? payload.review_status ?? '',
      reviewed: attestationPatch.reviewed ?? payload.reviewed === true,
      denominator_trust: attestationPatch.denominator_trust
        ?? payload.denominator_trust
        ?? liveCoverage.denominator_trust
        ?? null,
      baseline_observation_coverage_percent: attestationPatch.baseline_observation_coverage_percent
        ?? payload.baseline_observation_coverage_percent
        ?? liveCoverage.baseline_observation_coverage_percent
        ?? null,
      followup_observation_coverage_percent: attestationPatch.followup_observation_coverage_percent
        ?? payload.followup_observation_coverage_percent
        ?? liveCoverage.followup_observation_coverage_percent
        ?? null,
      ...attestationPatch,
    };
  }
  return {
    controls_present: attestationPatch.controls_present ?? payload.controls_present === true,
    controls_functioning: attestationPatch.controls_functioning ?? payload.controls_functioning === true,
    ...attestationPatch,
  };
}

export function buildVerificationMethodValidationSync(input: {
  verificationMethod: string;
}): { evidenceNote: string; resultPayload: Record<string, any> } | null {
  const verificationMethod = String(input.verificationMethod || '').trim();
  if (!verificationMethod) return null;
  const label = formatVerificationMethodLabel(verificationMethod);
  return {
    evidenceNote: `Verification method saved on the corrective action: ${label}.`,
    resultPayload: {
      verification_method: verificationMethod,
      derived_from_live_workflow: true,
    },
  };
}

export function buildCoverageReviewValidationSync(input: {
  evidenceReference?: string | null;
}): { evidenceNote: string; resultPayload: Record<string, any> } {
  const evidenceReference = String(input.evidenceReference || '').trim() || null;
  return {
    evidenceNote: evidenceReference
      ? `Coverage reliability reviewed in the live workflow. Evidence reference: ${evidenceReference}.`
      : 'Coverage reliability reviewed in the live workflow.',
    resultPayload: {
      review_status: 'reviewed',
      reviewed: true,
      evidence_reference: evidenceReference,
      derived_from_live_workflow: true,
    },
  };
}

export type LiveValidationContext = {
  verificationMethod?: string | null;
  workflowImpactSummary?: WorkflowImpactSummary | null;
  coverageReview?: {
    reviewed?: boolean;
    review_status?: string | null;
    evidence_reference?: string | null;
  } | null;
  zoneControlsConfirmation?: {
    controls_present?: boolean;
    controls_functioning?: boolean;
    evidence_reference?: string | null;
  } | null;
};

export function mergeLiveValidationControlState(
  blockerType: string,
  controlState?: ClaimReadinessTaskControlState | null,
  liveContext?: LiveValidationContext | null,
): ClaimReadinessTaskControlState | null | undefined {
  if (!liveContext) return controlState;
  const type = String(blockerType || '').trim().toLowerCase();
  const existing = mergedValidationControlPayload(controlState);
  let controlPayloadPatch: Record<string, any> = {};
  if (type === 'verification_method') {
    const verificationMethod = String(
      liveContext.verificationMethod
      || existing.verification_method
      || '',
    ).trim();
    if (verificationMethod) {
      controlPayloadPatch = { verification_method: verificationMethod };
    }
  }
  if (type === 'observation_coverage_reliability') {
    const liveCoverage = readLiveCoverageFields(liveContext.workflowImpactSummary);
    const reviewed = liveContext.coverageReview?.reviewed === true
      || String(liveContext.coverageReview?.review_status || '').trim().toLowerCase() === 'reviewed';
    controlPayloadPatch = {
      ...liveCoverage,
      ...(reviewed ? { reviewed: true, review_status: 'reviewed' } : {}),
    };
  }
  if (type === 'field_confirmation_of_zone_controls') {
    const confirmation = liveContext.zoneControlsConfirmation;
    if (confirmation?.controls_present === true && confirmation?.controls_functioning === true) {
      controlPayloadPatch = {
        controls_present: true,
        controls_functioning: true,
        evidence_reference: confirmation.evidence_reference ?? null,
      };
    }
  }
  if (!Object.keys(controlPayloadPatch).length) return controlState;
  return {
    ...(controlState || {}),
    control_payload: {
      ...(controlState?.control_payload || {}),
      ...controlPayloadPatch,
    },
  };
}

export type LiveValidationSyncContext = LiveValidationContext & {
  workflowId: string;
  reportId?: string | null;
};

export function resolveOpenValidationTask(
  response: WorkflowClaimReadinessResponse,
  blockerType: GroupedBlockerSyncType,
) {
  const controlState = response.controls_by_type?.[blockerType];
  const taskId = String(controlState?.task_id || '').trim();
  if (taskId) {
    return { taskId, controlState };
  }
  const workflows = Array.isArray(response.validation_workflows) ? response.validation_workflows : [];
  for (const workflow of workflows) {
    const blockersById = new Map(
      (Array.isArray(workflow?.blockers) ? workflow.blockers : []).map((blocker) => [
        String(blocker?.blocker_id || ''),
        blocker,
      ]),
    );
    const task = (Array.isArray(workflow?.tasks) ? workflow.tasks : []).find((value) => {
      const resolvedType = String(
        value?.blocker_type
        || blockersById.get(String(value?.blocker_id || ''))?.blocker_type
        || '',
      ).trim().toLowerCase();
      const status = String(value?.status || 'open').trim().toLowerCase();
      if (resolvedType !== blockerType) return false;
      return status === 'open' || status === 'in_progress';
    });
    if (task?.task_id) {
      return {
        taskId: String(task.task_id),
        controlState: controlState || response.controls_by_type?.[blockerType] || null,
      };
    }
  }
  return null;
}

export function buildZoneControlsValidationSync(input: {
  evidenceReference?: string | null;
}): { evidenceNote: string; resultPayload: Record<string, any> } {
  const evidenceReference = String(input.evidenceReference || '').trim() || null;
  return {
    evidenceNote: evidenceReference
      ? `Zone controls were confirmed in the live workflow. Evidence reference: ${evidenceReference}.`
      : 'Zone controls were confirmed in the live workflow.',
    resultPayload: {
      controls_present: true,
      controls_functioning: true,
      evidence_reference: evidenceReference,
      derived_from_live_workflow: true,
    },
  };
}

export function buildWorkflowCorrectiveActionTrackingLabel(action: {
  title?: string | null;
  action_type?: string | null;
  status?: string | null;
  verification_method?: string | null;
} | null | undefined): string | null {
  if (!action) return null;
  const title = String(action.title || action.action_type || '').trim();
  if (!title) return null;
  const status = String(action.status || '').trim().toLowerCase();
  const verificationMethod = formatVerificationMethodLabel(action.verification_method);
  const statusLabel = status ? status.replace(/_/g, ' ') : '';
  if (verificationMethod && statusLabel) {
    return `${title} recorded (${statusLabel}; verification: ${verificationMethod})`;
  }
  if (statusLabel) return `${title} recorded (${statusLabel})`;
  return `${title} recorded on workflow corrective action`;
}

export async function syncOpenValidationTasksFromLiveWorkflow(
  context: LiveValidationSyncContext,
): Promise<boolean> {
  const workflowKey = String(context.workflowId || '').trim();
  if (!workflowKey) return false;
  const response = await reportsApi.getWorkflowClaimReadiness(workflowKey, context.reportId ?? null);
  if (!response?.ok) return false;

  const candidates: Array<{
    blockerType: GroupedBlockerSyncType;
    sync: { evidenceNote: string; resultPayload: Record<string, any> } | null;
  }> = [
    {
      blockerType: 'verification_method',
      sync: buildVerificationMethodValidationSync({
        verificationMethod: String(context.verificationMethod || '').trim(),
      }),
    },
    {
      blockerType: 'observation_coverage_reliability',
      sync: (
        context.coverageReview?.reviewed === true
        || String(context.coverageReview?.review_status || '').trim().toLowerCase() === 'reviewed'
      )
        ? buildCoverageReviewValidationSync({
          evidenceReference: context.coverageReview?.evidence_reference,
        })
        : null,
    },
    {
      blockerType: 'field_confirmation_of_zone_controls',
      sync: (
        context.zoneControlsConfirmation?.controls_present === true
        && context.zoneControlsConfirmation?.controls_functioning === true
      )
        ? buildZoneControlsValidationSync({
          evidenceReference: context.zoneControlsConfirmation?.evidence_reference,
        })
        : null,
    },
  ];

  let synced = false;
  for (const candidate of candidates) {
    if (!candidate.sync) continue;
    const resolved = resolveOpenValidationTask(response, candidate.blockerType);
    if (!resolved?.taskId) continue;
    const resultPayload = buildBlockerSyncResultPayload(
      candidate.blockerType,
      resolved.controlState,
      candidate.sync.resultPayload,
      context.workflowImpactSummary,
    );
    await reportsApi.patchValidationTask(String(resolved.taskId), {
      status: 'completed',
      evidence_note: candidate.sync.evidenceNote,
      result_payload: resultPayload,
    });
    synced = true;
  }
  return synced;
}
